import { observable, flow } from 'mobx';
import dayjs from 'dayjs';
import queryString from 'query-string';

import {
    Config,
    DAY_MONTH_YEAR_FORMAT,
    navigateWindowTo,
    noticeError,
    timeoutAsPromise,
    waitForDocumentUpdateUntil,
} from '@ratehub/base-ui';
import { GENDERS, GENDERS_CODES } from '@ratehub/documents/common/stable';

import PAGES from '../definitions/Pages';
import { InsLifeWizardFormTypes } from '../definitions/CurrentDocument';
import PROVIDER_IDS from '../definitions/ProviderIds';


function createPersonalDetailsPageVM(context) {
    if (!context) {
        throw new RangeError('Context provided to view model is empty', 'createPersonalDetailsPageVM.js');
    }

    const state = observable({
        context,

        name: PAGES.PERSONAL_DETAILS.id,
        hasBeenVisited: false,
        isCompleted: false,

        get isActive() {
            return context.viewStore?.activePageName === state.name;
        },

        get shouldShowGenderQuestion() {
            return context.journey?.formType !== InsLifeWizardFormTypes.GROUP;
        },

        get values() {
            return {
                get name() {
                    return state.name;
                },

                // Applicant values
                get firstName() {
                    return context.journey?.applicant?.firstName;
                },
                get lastName() {
                    return context.journey?.applicant?.lastName;
                },
                get gender() {
                    return context.journey?.applicant?.gender;
                },
                get dateOfBirth() {
                    return context.journey?.applicant?.dob;
                },
                get phone() {
                    return context.journey?.applicant?.phone;
                },
                get email() {
                    return context.journey?.applicant?.email;
                },
                get wantsEmailUpdates() {
                    return context.journey?.wantsEmailUpdates;
                },

                // Joint applicant values
                get jointApplicantFirstName() {
                    return context.journey?.jointApplicant?.firstName;
                },
                get jointApplicantLastName() {
                    return context.journey?.jointApplicant?.lastName;
                },
                get jointApplicantGender() {
                    return context.journey?.jointApplicant?.gender;
                },
                get jointApplicantDateOfBirth() {
                    return context.journey?.jointApplicant?.dob;
                },
                get hasJointApplicantConsumedTobacco() {
                    return context.journey?.jointApplicant?.hasConsumedTobacco;
                },
            };
        },

        submit: flow(function*(formValues) {
            // Prevent submission if already in progress
            if (context.isSubmittingLead) {
                return;
            }

            // Save values to the document.
            context.updateJourney(transformValuesToModel(formValues));

            // We want to allow the user to push a new lead any time there is a change to their information
            if (context.sessionStore.hasDocumentUnsavedChanges(context.activeDocument.id)) {
                context.setShouldPushNewLead(true);
            }

            // Prevent submitting duplicate leads
            if (!context.shouldPushNewLead) {
                context.viewStore.showConfirmationPage();
                return;
            }

            // Start lead submission
            context.setIsSubmittingLead(true);
            
            // Create a new inquiry (this will cause a lead to be submitted).
            context.submitInquiry();

            try {
                // If auto save is turned off, we can't poll the document for a providerId
                if (!context.isAutoSaveEnabled) {
                    throw new Error('autoSave is disabled. Unable to poll document for providerId');
                }

                // Wait for the SessionStore to finish saving to the Profile Service.
                yield context.sessionStore.waitForDocumentToFinishSaving(context.activeDocument.id);

                // We're about to receive mutations from the server; we don't want them to get replayed back to the server.
                context.setIsAutoSaveEnabled(false);

                // Poll until our document contains a providerId, or we timeout.
                yield Promise.all([
                    waitForDocumentUpdateUntil(
                        context.activeDocument,
                        'insurance.life',
                        (document) => document.journey.providerId != null,
                        { timeout: 8000 }, // give up after 8 seconds
                    ),
                    timeoutAsPromise(2000), // don't want the spinner to show for less than 2 seconds
                ]);

                // Submission is complete, and now we have our assigned providerId.
                state.isCompleted = true;
                context.setShouldPushNewLead(false);

                if (context.provider.id === PROVIDER_IDS.POLICY_ME) {
                    handlePolicyMeSubmission(context);
                } else {
                    context.viewStore.showConfirmationPage();
                    context.setIsSubmittingLead(false);
                }
            } catch (error) {
                noticeError(
                    error,
                    {
                        documentId: context.activeDocument.id,
                        message: '[submit] error submitting lead',
                    },
                );

                context.setHasError(true);
                context.setIsSubmittingLead(false);
            } finally {
                context.setIsAutoSaveEnabled(true);
            }
        }),
        
    });

    return state;
}

/**
 * Transform the form values into the correct shape to submit to the document
 * @param {object} values form values to update with
 */
function transformValuesToModel(values) {
    return {
        applicant: {
            firstName: values.firstName,
            lastName: values.lastName,
            gender: values.gender,
            dob: values.dateOfBirth,
            phone: values.phone,
            email: values.email,
        },
        jointApplicant: {
            firstName: values.jointApplicantFirstName,
            lastName: values.jointApplicantLastName,
            gender: values.jointApplicantGender,
            dob: values.jointApplicantDateOfBirth,
            hasConsumedTobacco: values.hasJointApplicantConsumedTobacco,
        },
        wantsEmailUpdates: false, // Defaulting to false as of INS-3345
    };
}

// PolicyMe
function handlePolicyMeSubmission(context) {
    const primaryApplicant = getParams(context.journey.applicant);
    const secondaryApplicant = getParams(context.journey.jointApplicant);
    const isJointPolicy = context.journey.isJointPolicy;

    const search = queryString.stringify({
        ...primaryApplicant,
        utm_source: 'RatehubTerm', // Requirement from PolicyMe INS-3828
        partner: isJointPolicy,
        ...(isJointPolicy
            ? {
                partner_birthdate: secondaryApplicant.birthdate,
                partner_smoker: secondaryApplicant.smoker,
                partner_gender: secondaryApplicant.gender,
            }
            : {}
        ),
    });

    navigateWindowTo(`${Config.POLICYME_BASE_URL}?${search}`);
}

function getParams(applicant) {
    return applicant && {
        birthdate: policyMeMapping.formatBirthdate(applicant.dob),
        smoker: policyMeMapping.formatBoolean(applicant.hasConsumedTobacco),
        gender: policyMeMapping.formatGender(applicant.gender),
    };
}

const policyMeMapping = {
    formatBoolean(value) {
        return value ? 'T' : 'F';
    },
    formatGender(value) {
        return GENDERS_CODES[value] === GENDERS_CODES[GENDERS.OTHERS]
            ? GENDERS_CODES[GENDERS.MALE]
            : GENDERS_CODES[value];
    },
    formatBirthdate(value) {
        return dayjs(value).format(DAY_MONTH_YEAR_FORMAT).replace(/\//g, '');
    },
};

export default createPersonalDetailsPageVM;
